import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { Point, PointCoordinate, Size } from "PosterEditor/model/poster-types";

export const coordinatesToPoints = (
  map: any,
  scale: number,
  coordinates: Coordinate[]
): Point[] => {
  const points = coordinates
    .map((coord) => {
      if (coord.lat === undefined || coord.lng === undefined) {
        console.error("Coordinate is missing lat or lng");
        return null;
      }
      return map.current.project([coord.lng, coord.lat]) as Point;
    })
    .filter((point) => point !== null) as Point[];
  const scaled = points.map((point) => ({
    x: point.x * scale,
    y: point.y * scale,
  }));
  return scaled;
};

export const coordinatesToPointCoordinates = (
  map: any,
  scale: number,
  coordinates: Coordinate[]
): PointCoordinate[] => {
  const points = coordinates
    .map((coord) => {
      if (coord.lat === undefined || coord.lng === undefined) {
        console.error("Coordinate is missing lat or lng");
        return null;
      }
      const point = map.current.project([coord.lng, coord.lat]) as Point;
      return {
        point,
        coordinate: coord,
      };
    })
    .filter((point) => point !== null) as PointCoordinate[];
  return points;
};

export const splitCoordinatesWithInterupt = (
  map: any,
  originalMapSize: Size,
  canvasSize: Size,
  coordinates: Coordinate[],
  pointerLocation: Point,
  interuptRadius: number
): {
  lineSegments: Point[][];
  interuptSegments: Point[][];
  closestPointToCursor?: PointCoordinate;
} => {
  const scale = canvasSize.width / originalMapSize.width;
  const points = coordinatesToPointCoordinates(map, scale, coordinates);
  return splitPointsWithInterupt(points, pointerLocation, interuptRadius);
};

export const closestPointCoordinateToCursor = (
  map: React.MutableRefObject<any>,
  coordinates: Coordinate[],
  pointerLocation: Point,
  // If curser is futher away than interrupt radius, returns null.
  pointerInterupRadius: number
): PointCoordinate | null => {
  const points = coordinatesToPoints(map, 1, coordinates);
  let closestIndex: number | null = null;
  let distanceToClosestPoint: number | undefined;

  for (let i = 0; i < points.length - 1; i++) {
    const point = points[i];
    if (point.x === undefined || point.y === undefined) {
      // console.error("Point is missing x or y");
      continue;
    }
    const distance = Math.sqrt(
      Math.pow(pointerLocation.x - point.x, 2) +
        Math.pow(pointerLocation.y - point.y, 2)
    );
    if (
      (!distanceToClosestPoint || distance < distanceToClosestPoint) &&
      distance < pointerInterupRadius
    ) {
      closestIndex = i;
      distanceToClosestPoint = distance;
    }
  }
  if (!closestIndex) {
    return null;
  }
  const pointCoordinate: PointCoordinate = {
    point: points[closestIndex],
    coordinate: coordinates[closestIndex],
  };
  return pointCoordinate;
};

export const splitPointsWithInterupt = (
  projectedCoordinates: PointCoordinate[],
  pointerLocation: Point,
  interuptRadius: number
): {
  lineSegments: Point[][];
  interuptSegments: Point[][];
  closestPointToCursor?: PointCoordinate;
} => {
  let lineSegments: Point[][] = [];
  let interuptSegments: Point[][] = [];

  let closestPointToCursor: PointCoordinate | undefined;
  let distanceToClosestPoint: number | undefined;

  var wasInside = false;
  for (let i = 0; i < projectedCoordinates.length - 1; i++) {
    const pointCoordinate = projectedCoordinates[i];
    if (
      pointCoordinate.point.x === undefined ||
      pointCoordinate.point.y === undefined
    ) {
      // console.error("Point is missing x or y");
      continue;
    }
    const distance = Math.sqrt(
      Math.pow(pointerLocation.x - pointCoordinate.point.x, 2) +
        Math.pow(pointerLocation.y - pointCoordinate.point.y, 2)
    );
    if (!distanceToClosestPoint || distance < distanceToClosestPoint) {
      closestPointToCursor = pointCoordinate;
      distanceToClosestPoint = distance;
    }
    const isFirstPoint = i === 0;
    if (distance < interuptRadius) {
      if (isFirstPoint || !wasInside || interuptSegments.length === 0) {
        const newArray = [];
        if (!wasInside && i > 0) {
          //Add the last point outside the radius to maintain a smooth line and not break between segments.
          newArray.push(projectedCoordinates[i - 1].point);
        }
        newArray.push(pointCoordinate.point);
        interuptSegments.push(newArray);
      } else {
        interuptSegments[interuptSegments.length - 1].push(
          pointCoordinate.point
        );
      }
      wasInside = true;
    } else {
      if (wasInside) {
        //Add this point to last interrupted segment to mainain the line
        if (interuptSegments.length > 0) {
          let segmentArray = interuptSegments[interuptSegments.length - 1];
          segmentArray.push(pointCoordinate.point);
        }
      }
      if (isFirstPoint || wasInside || lineSegments.length === 0) {
        const newArray = [pointCoordinate.point];
        lineSegments.push(newArray);
      } else {
        lineSegments[lineSegments.length - 1].push(pointCoordinate.point);
      }
      wasInside = false;
    }
  }
  return { lineSegments, interuptSegments, closestPointToCursor };
};

export const distanceBetweenPoints = (point1: Point, point2: Point): number => {
  return Math.sqrt(
    Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2)
  );
};
